import {
  CalciteLabel,
  CalciteRadioButton,
  CalciteRadioButtonGroup,
} from "@esri/calcite-components-react";
import { Button } from "components/Button";
import { Modal } from "components/Modal";
import {
  NOTIFICATION_TYPE,
  NotificationContext,
} from "context/NotificationContext";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Criteria } from "services/APIBase.d";
import RecordService from "services/RecordService";
import { Input } from "./Input";

type ALIExportProps = {
  exportColumns?: { [key: string]: string };
  type?: string;
  criterias: Criteria[];
  additionalOptions?: { [key: string]: any };
  buttonName?: string;
  showCompleted?: boolean;
  questionOnly?: boolean;
  dateRange?: {
    start: string | null;
    end: string | null;
  };
};

const aliRecordsClient = new RecordService("ali");
const msagRecordsClient = new RecordService("msag");
const aliErrorClient = new RecordService("ali/error");
const eltRecordsClient = new RecordService("elt");
const discrepancyClient = new RecordService("discrep");

function defaultNenaOptions() {
  return {
    v21header: true,
    company_name: "",
    cycle_counter: "",
    county_id: "",
    state: "",
  };
}

const formatOptions = {
  CSV: "csv",
  "NENAv2.1": "v2.1",
  Excel: "xlsx",
};

const labels = {
  "ali-record": "ALI",
  "msag-record": "MSAG",
  "ali-error": "ALI Error",
  "elt-record": "ELT",
  discrepancy: "Discrepancy",
  statistical: "Discrepancy",
};

const ExportButton: React.FC<ALIExportProps> = ({
  exportColumns = {},
  type = "ali-record",
  criterias = [],
  additionalOptions,
  buttonName = "Export as Filtered",
  showCompleted = false,
  questionOnly = false,
  dateRange: { start, end } = { start: null, end: null },
}: ALIExportProps) => {
  const { addNotification, dismissNotification } =
    useContext(NotificationContext);

  const [open, setOpen] = React.useState(false);
  const [format, setFormat] = React.useState(
    type === "statistical"
      ? Object.values(formatOptions)[2]
      : Object.values(formatOptions)[0],
  );
  const [columns, setColumns] = React.useState<string[]>(
    Object.values(exportColumns),
  );
  const [nenaOptions, setNenaOptions] = React.useState(defaultNenaOptions());

  const [startDate, setStartDate] = useState<string | null>(
    start || getDefaultStartDate(),
  );
  const [endDate, setEndDate] = useState<string | null>(
    end || getDefaultEndDate(),
  );

  function getDefaultStartDate() {
    // return november 1st 2020 00:00:00
    return new Date(2020, 10, 1).toISOString().split("T")[0];
  }

  function getDefaultEndDate() {
    // return today at 23:59:59
    return new Date().toISOString().split("T")[0];
  }

  const services = React.useMemo(
    () => ({
      "ali-record": aliRecordsClient,
      "msag-record": msagRecordsClient,
      "ali-error": aliErrorClient,
      "elt-record": eltRecordsClient,
      statistical: discrepancyClient,
      discrepancy: discrepancyClient,
    }),
    [],
  );

  const handleClose = React.useCallback(() => {
    setFormat(Object.values(formatOptions)[0]);
    setColumns(Object.values(exportColumns));
    setNenaOptions(defaultNenaOptions());
    setOpen(false);
  }, [exportColumns]);

  const handleDownload = useCallback(
    (response, format) => {
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type}-export-${format}-${new Date()
        .toISOString()
        .replace(
          /[:.]/g,
          "-",
        )}${format === "csv" ? ".csv" : format === "v2.1" ? ".txt" : ".xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    },
    [type],
  );

  const validateRequest = useCallback(() => {
    if (format === "csv") {
      if (type === "statistical") {
        if (!startDate || !endDate) return false;
      } else {
        if (columns.length < 1) return false;
      }
    }

    return true;
  }, [format, columns, type, startDate, endDate]);

  const handleExport = useCallback(
    (f, c) => {
      dismissNotification();
      handleClose();
      addNotification({
        title: "Exporting...",
        message: "Your export is in progress.",
        type: NOTIFICATION_TYPE.LOADING,
      });
      services[type]
        .exportRecords(f, c, criterias, showCompleted, questionOnly, {
          start,
          end,
        })
        .then((response) => {
          setTimeout(() => {
            handleDownload(response, f);
            dismissNotification();
            addNotification({
              title: "Export Successful",
              message: "Your data has been exported successfully.",
              type: NOTIFICATION_TYPE.SUCCESS,
            });
          }, 500);
        })
        .catch((err) => {
          console.log(err);
          dismissNotification();
          addNotification({
            title: "Export Failed",
            message: "There was an error exporting your data: " + err.message,
            type: NOTIFICATION_TYPE.ERROR,
          });
        });
    },
    [
      addNotification,
      criterias,
      dismissNotification,
      handleClose,
      handleDownload,
      services,
      type,
    ],
  );

  const handleStatisticalExport = useCallback(
    (f, c) => {
      dismissNotification();
      handleClose();
      addNotification({
        title: "Exporting...",
        message: "Your export is in progress.",
        type: NOTIFICATION_TYPE.LOADING,
      });
      services[type]
        .exportStatisticalReport(f, { startDate, endDate })
        .then((response) => {
          setTimeout(() => {
            handleDownload(response, f);
            dismissNotification();
            addNotification({
              title: "Export Successful",
              message: "Your data has been exported successfully.",
              type: NOTIFICATION_TYPE.SUCCESS,
            });
          }, 500);
        })
        .catch((err) => {
          console.log(err);
          dismissNotification();
          addNotification({
            title: "Export Failed",
            message: "There was an error exporting your data: " + err.message,
            type: NOTIFICATION_TYPE.ERROR,
          });
        });
    },
    [
      addNotification,
      dismissNotification,
      handleClose,
      handleDownload,
      services,
      type,
      startDate,
      endDate,
    ],
  );

  const exportRecords = useCallback(() => {
    if (type === "statistical") {
      handleStatisticalExport(format, { startDate, endDate });
    } else if (format === "csv") handleExport(format, columns);
    else if (format === "v2.1") handleExport(format, nenaOptions);
  }, [
    format,
    handleExport,
    columns,
    nenaOptions,
    handleStatisticalExport,
    type,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    setFormat(
      type === "statistical"
        ? Object.values(formatOptions)[2]
        : Object.values(formatOptions)[0],
    );

    setStartDate(start || getDefaultStartDate());
    setEndDate(end || getDefaultEndDate());
  }, [type, start, end]);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Enter" && open && validateRequest()) {
        e.preventDefault();
        exportRecords();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return function cleanup() {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, validateRequest, exportRecords]);

  return (
    <div style={{ display: "flex", justifyContent: "flex-end" }}>
      <Modal
        isOpen={open}
        kind="brand"
        title={
          type === "statistical"
            ? `Export Statistical Report`
            : `Export ${labels[type]} Records`
        }
        onClose={handleClose}
        isFullScreen={undefined}
        secondary={
          <Button onClick={handleClose} kind="neutral">
            Close
          </Button>
        }
        primary={
          <Button
            onClick={exportRecords}
            disabled={validateRequest() === false}
            kind="brand"
          >
            Download
          </Button>
        }
        content={
          <>
            <CalciteLabel>
              Format
              <CalciteRadioButtonGroup
                layout="horizontal"
                name="Export Options"
              >
                {Object.keys(formatOptions)
                  .filter((option) => {
                    // Hide NENAv2.1 for certain types
                    if (
                      (type === "ali-error" ||
                        type === "elt-record" ||
                        type === "discrepancy" ||
                        type === "statistical") &&
                      option === "NENAv2.1"
                    ) {
                      return false;
                    }
                    // Only show Excel for statistical export
                    if (option === "Excel" && type !== "statistical") {
                      return false;
                    }
                    // Remove CSV for statistical export
                    if (option === "CSV" && type === "statistical") {
                      return false;
                    }
                    // Show all other options
                    return true;
                  })
                  .map((option) => (
                    <CalciteLabel
                      key={option}
                      layout="inline"
                      style={{ cursor: "pointer" }}
                    >
                      {option}
                      <CalciteRadioButton
                        value={formatOptions[option]}
                        checked={format === formatOptions[option]}
                        onCalciteRadioButtonChange={() =>
                          setFormat(formatOptions[option])
                        }
                      />
                    </CalciteLabel>
                  ))}
              </CalciteRadioButtonGroup>
            </CalciteLabel>
            {type !== "statistical" ? (
              <>
                <hr
                  style={{
                    marginBottom: "1rem",
                    borderWidth: "2px",
                  }}
                />
                {format === "csv" ? (
                  <>
                    <div
                      style={{
                        display: "inline-block",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <CalciteLabel
                        onClick={() => {
                          if (
                            columns.length === Object.keys(exportColumns).length
                          ) {
                            setColumns([]);
                          } else {
                            setColumns(Object.values(exportColumns));
                          }
                        }}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        Columns
                      </CalciteLabel>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: "0.5rem 1rem",
                        marginTop: "0.5rem",
                      }}
                    >
                      {Object.keys(exportColumns)
                        .sort((a, b) => a.localeCompare(b))
                        .map((column) => (
                          <CalciteLabel
                            key={column}
                            layout="inline"
                            style={{ cursor: "pointer", userSelect: "none" }}
                            onClick={() => {
                              if (columns.includes(exportColumns[column])) {
                                setColumns(
                                  columns.filter(
                                    (col) => col !== exportColumns[column],
                                  ),
                                );
                              } else {
                                setColumns([...columns, exportColumns[column]]);
                              }
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={columns.includes(exportColumns[column])}
                              onChange={(e) => {
                                // Prevent double toggling since onClick on label also toggles
                                e.stopPropagation();
                                if (e.target.checked) {
                                  setColumns([
                                    ...columns,
                                    exportColumns[column],
                                  ]);
                                } else {
                                  setColumns(
                                    columns.filter(
                                      (col) => col !== exportColumns[column],
                                    ),
                                  );
                                }
                              }}
                              style={{ marginRight: "0.5rem" }}
                              onClick={(e) => e.stopPropagation()} // Prevent label onClick from firing
                            />
                            {column}
                          </CalciteLabel>
                        ))}
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: "1rem",
                    }}
                  >
                    <div>
                      <Input
                        label="Company Name"
                        value={nenaOptions.company_name}
                        onChange={(e) =>
                          setNenaOptions({
                            ...nenaOptions,
                            company_name: e.target.value,
                          })
                        }
                        max={50}
                        name={"companyName"}
                      />
                    </div>
                    <div>
                      <Input
                        label="Cycle Counter"
                        value={nenaOptions.cycle_counter}
                        onChange={(e) =>
                          setNenaOptions({
                            ...nenaOptions,
                            cycle_counter: e.target.value,
                          })
                        }
                        name={"cycleCounter"}
                        max={9}
                      />
                    </div>
                    <div>
                      <Input
                        label="County ID"
                        value={nenaOptions.county_id}
                        onChange={(e) =>
                          setNenaOptions({
                            ...nenaOptions,
                            county_id: e.target.value,
                          })
                        }
                        name={"countyId"}
                        max={4}
                      />
                    </div>
                    <div>
                      <Input
                        label="State"
                        value={nenaOptions.state}
                        onChange={(e) =>
                          setNenaOptions({
                            ...nenaOptions,
                            state: e.target.value,
                          })
                        }
                        name={"state"}
                        max={2}
                      />
                    </div>
                    <div>
                      <CalciteLabel
                        layout="inline"
                        style={{
                          cursor: "pointer",
                          marginTop: "10%",
                          userSelect: "none",
                        }}
                        onClick={() =>
                          setNenaOptions({
                            ...nenaOptions,
                            v21header: !nenaOptions.v21header,
                          })
                        }
                      >
                        <input
                          type="checkbox"
                          checked={nenaOptions.v21header}
                          onChange={(e) =>
                            setNenaOptions({
                              ...nenaOptions,
                              v21header: e.target.checked,
                            })
                          }
                          style={{ marginRight: "0.5rem" }}
                        />
                        Header and Trailer
                      </CalciteLabel>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <CalciteLabel>Date Range</CalciteLabel>
                <div style={{ display: "flex", gap: "1rem" }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: "0.8rem" }}>From:</p>
                    <input
                      type="date"
                      style={{
                        padding: "0.5rem",
                        border: "1px solid #ccc",
                        borderRadius: "0.375rem",
                        fontSize: "0.875rem",
                        color: startDate ? "black" : "#999",
                        width: "100%",
                      }}
                      placeholder="Start Occurred"
                      onChange={(e) => {
                        setStartDate(e.target.value);
                      }}
                      name="start"
                      value={startDate ? startDate : ""}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: "0.8rem" }}>To:</p>
                    <input
                      type="date"
                      style={{
                        padding: "0.5rem",
                        border: "1px solid #ccc",
                        borderRadius: "0.375rem",
                        fontSize: "0.875rem",
                        color: endDate ? "black" : "#999",
                        width: "100%",
                      }}
                      placeholder="End Occurred"
                      onChange={(e) => {
                        setEndDate(e.target.value);
                      }}
                      name="end"
                      value={endDate ? endDate : ""}
                    />
                  </div>
                </div>
              </>
            )}
            {additionalOptions && (
              <>
                <hr
                  style={{
                    marginBottom: "1rem",
                    borderWidth: "2px",
                  }}
                />
                <div>
                  {Object.entries(additionalOptions).map(([key, value]) => (
                    <div key={key}>
                      <strong>{key}:</strong> {value}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        }
      />

      {/* Button to open modal */}
      <Button
        variant="solid"
        iconStart="download"
        kind="brand"
        onClick={() => setOpen(true)}
      >
        {buttonName}
      </Button>
    </div>
  );
};

export default ExportButton;
